/**
 * Menu Page - Tailwind Version
 *
 * Professional Uber Eats Style Menu Maker.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Plus,
  Search,
  RefreshCw,
  ChefHat,
  Check,
  ChevronDown,
  ChevronRight,
  Pencil,
  Eye,
  EyeOff,
  LayoutGrid,
  List,
  Info,
  Clock,
  Download,
  MoreVertical,
  Layers,
} from "lucide-react";

import { Dish, DishCategory, DishOption } from "@tasty/api";
import {
  DishClient,
  DishCategoryClient,
  DishOptionClient,
} from "@tasty/api/client";
import { useRestaurantContext } from "../../context/restaurant-context";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Card, CardContent } from "../../components/ui/card";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../components/ui/tabs";
import { Sheet } from "../../components/ui/sheet";
import { DishEditorDrawer } from "./DishEditorDrawer";
import { CategoryEditorDrawer } from "./CategoryEditorDrawer";
import { OptionEditorDrawer } from "./OptionEditorDrawer";
import { cn, formatPrice } from "../../lib/utils";
import { TERMS } from "@tasty/shared";
import { toast } from "sonner";

export default function MenuPage() {
  const { restaurant } = useRestaurantContext();

  const [loading, setLoading] = useState(true);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [dishes, setDishes] = useState<Dish[]>([]);
  const [categories, setCategories] = useState<DishCategory[]>([]);
  const [options, setOptions] = useState<DishOption[]>([]);
  
  const [activeTab, setActiveTab] = useState("overview");
  const [searchTerm, setSearchTerm] = useState("");
  const [collapsedCategoryIds, setCollapsedCategoryIds] = useState<Set<string>>(new Set());

  // Side Panel States
  const [isDishEditorOpen, setIsDishEditorOpen] = useState(false);
  const [editingDishId, setEditingDishId] = useState<string | null>(null);
  
  const [isCategoryEditorOpen, setIsCategoryEditorOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<DishCategory | null>(null);

  const [isOptionEditorOpen, setIsOptionEditorOpen] = useState(false);
  const [editingOption, setEditingOption] = useState<DishOption | null>(null);

  // In-line Price Editing
  const [inlinePriceEditingId, setInlinePriceEditingId] = useState<string | null>(null);
  const [inlinePriceValue, setInlinePriceValue] = useState("");

  const fetchData = useCallback(async () => {
    if (!restaurant?.id) return;
    setLoading(true);
    try {
      const [dishesData, categoriesData, optionsData] = await Promise.all([
        DishClient.queryList({ filter: { restaurantID: { eq: restaurant.id } } }),
        DishCategoryClient.queryList({ filter: { restaurantID: { eq: restaurant.id } } }),
        DishOptionClient.queryList({ filter: { restaurantID: { eq: restaurant.id } } }),
      ]);
      setDishes(dishesData || []);
      setCategories((categoriesData || []).slice().sort((a, b) => (a.order || 0) - (b.order || 0)));
      setOptions(optionsData || []);
    } catch (error) {
      toast.error("Fehler beim Laden");
    } finally {
      setLoading(false);
    }
  }, [restaurant?.id]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // In-line Price Logic with Guard
  const startInlinePriceEdit = (dish: Dish) => {
    setInlinePriceEditingId(dish.id);
    setInlinePriceValue(String(dish.price || ""));
  };

  const saveInlinePrice = async (dish: Dish) => {
    const val = inlinePriceValue.replace(",", ".").trim();
    const p = parseFloat(val);
    
    if (isNaN(p) || p < 0) {
      setInlinePriceEditingId(null);
      return;
    }
    if (p === dish.price) {
      setInlinePriceEditingId(null);
      return;
    }

    setInlinePriceEditingId(null); // Immediate UI feedback
    setBulkBusy(true);
    try {
      const updated = await DishClient.updateItem({ id: dish.id, price: p, _version: dish._version });
      if (updated) {
        setDishes(prev => prev.map(d => d.id === dish.id ? updated : d));
        toast.success("Preis aktualisiert");
      }
    } catch {
      toast.error("Fehler beim Speichern");
      fetchData(); // Rollback
    } finally {
      setBulkBusy(false);
    }
  };

  const dishesByCategory = useMemo(() => {
    const grouped: Record<string, Dish[]> = {};
    const filtered = dishes.filter(d => 
      !searchTerm || d.name?.toLowerCase().includes(searchTerm.toLowerCase()) || d.description?.toLowerCase().includes(searchTerm.toLowerCase())
    );
    filtered.forEach(d => {
      const catId = d.dishCategoryId || "uncategorized";
      if (!grouped[catId]) grouped[catId] = [];
      grouped[catId].push(d);
    });
    return grouped;
  }, [dishes, searchTerm]);

  const toggleSoldOut = async (dish: Dish) => {
    try {
      const updated = await DishClient.updateItem({ id: dish.id, soldOut: !dish.soldOut, _version: dish._version });
      if (updated) {
        setDishes(prev => prev.map(d => d.id === dish.id ? updated : d));
        toast.success(dish.soldOut ? "Verfügbar" : "Ausverkauft");
      }
    } catch { toast.error("Fehler"); }
  };

  return (
    <div className="w-full space-y-10 animate-in fade-in duration-500">
      {/* Uber Sub-Header Section */}
      <div className="flex flex-col gap-8">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 border-b pb-4">
    <div className="space-y-6">
            <h1 className="text-3xl font-black tracking-tight">{TERMS.catalog}</h1>
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-auto">
              <TabsList className="bg-transparent p-0 h-auto gap-10 border-none">
                <TabsTrigger value="overview" className="rounded-none border-b-2 border-transparent data-[state=active]:border-foreground data-[state=active]:bg-transparent px-0 pb-3 h-auto font-bold text-sm text-muted-foreground data-[state=active]:text-foreground transition-all">Overview</TabsTrigger>
                <TabsTrigger value="categories" className="rounded-none border-b-2 border-transparent data-[state=active]:border-foreground data-[state=active]:bg-transparent px-0 pb-3 h-auto font-bold text-sm text-muted-foreground data-[state=active]:text-foreground transition-all">Categories</TabsTrigger>
                <TabsTrigger value="customizations" className="rounded-none border-b-2 border-transparent data-[state=active]:border-foreground data-[state=active]:bg-transparent px-0 pb-3 h-auto font-bold text-sm text-muted-foreground data-[state=active]:text-foreground transition-all">Customisations</TabsTrigger>
              </TabsList>
            </Tabs>
      </div>

          <div className="flex items-center gap-2 mb-2">
            <Button variant="ghost" size="icon" className="rounded-full"><Download className="w-4 h-4" /></Button>
            <Button variant="outline" size="sm" className="rounded-lg font-bold border-muted-foreground/20 ml-2" onClick={() => fetchData()}>Refresh list</Button>
        </div>
        </div>
      </div>

      <Tabs value={activeTab} className="w-full">
        <TabsContent value="overview" className="mt-0 space-y-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="bg-muted/30 px-3 py-1.5 rounded-xl border border-muted-foreground/5">
                <h2 className="text-xl font-black tracking-tight">Active Menu</h2>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                <Input placeholder={`Search ${dishes.length} products`} value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-9 h-9 bg-muted/20 border-none rounded-lg w-64 text-xs font-semibold" />
              </div>
              <Button onClick={() => { setEditingDishId(null); setIsDishEditorOpen(true); }} size="sm" className="h-9 px-4 rounded-lg font-bold ml-2">
                <Plus className="h-4 w-4 mr-2" /> Add item
              </Button>
            </div>
        </div>

          <div className="space-y-12 pb-20">
            {Object.entries(dishesByCategory).map(([catId, catDishes]) => {
              const cat = categories.find(c => c.id === catId);
              const collapsed = collapsedCategoryIds.has(catId);
              
              return (
                <div key={catId} className="space-y-4">
                  <div className="flex items-center justify-between group">
                    <div className="flex items-center gap-3">
                      <button onClick={() => setCollapsedCategoryIds(prev => { const n = new Set(prev); if(n.has(catId)) n.delete(catId); else n.add(catId); return n; })} className="h-5 w-5 rounded hover:bg-muted flex items-center justify-center transition-colors">
                        <ChevronDown className={cn("h-4 w-4 text-muted-foreground transition-transform duration-200", collapsed && "-rotate-90")} />
                    </button>
                      <h3 className="text-sm font-black uppercase tracking-widest">{cat?.name || "Uncategorized"}</h3>
                      <Badge variant="secondary" className="h-4 px-1.5 text-[9px] font-black opacity-40 rounded-sm">{catDishes.length}</Badge>
                    </div>
                    <Button variant="ghost" size="sm" className="opacity-0 group-hover:opacity-100 transition-opacity text-[10px] font-black text-muted-foreground uppercase tracking-widest h-7 px-2" onClick={() => { setEditingCategory(cat || null); setIsCategoryEditorOpen(true); }}>Edit Category</Button>
                  </div>

                  {!collapsed && (
                    <div className="grid grid-cols-1 gap-px border rounded-2xl overflow-hidden bg-muted-foreground/10 border-muted-foreground/10 shadow-sm">
                      {catDishes.map(dish => (
                        <div key={dish.id} className="group flex items-center justify-between p-3 bg-background hover:bg-muted/5 transition-all cursor-pointer border-b last:border-b-0 border-muted-foreground/5" onClick={() => { setEditingDishId(dish.id); setIsDishEditorOpen(true); }}>
                          <div className="flex items-center gap-4 min-w-0 flex-1">
                            <div className="h-10 w-10 rounded-lg bg-muted/20 flex items-center justify-center shrink-0 border border-muted-foreground/5">
                              {dish.image ? <img src={dish.image} className="h-full w-full object-cover" alt="" /> : <ChefHat className="h-5 w-5 text-muted-foreground/20" />}
                            </div>
                            <div className="min-w-0 flex-1 pr-4">
                              <span className="font-bold text-sm truncate block leading-tight group-hover:text-primary transition-colors">{dish.name}</span>
                              <p className="text-[10px] text-muted-foreground line-clamp-1 font-medium mt-0.5">{dish.description}</p>
                            </div>
                          </div>

                          <div className="flex items-center gap-6 ml-4" onClick={e => e.stopPropagation()}>
                            <div className="w-20 text-right">
                              {inlinePriceEditingId === dish.id ? (
                                <div className="relative">
                                  <Input value={inlinePriceValue} onChange={e => setInlinePriceValue(e.target.value)} onBlur={() => saveInlinePrice(dish)} onKeyDown={e => e.key === 'Enter' && saveInlinePrice(dish)} autoFocus className="h-8 w-full text-right font-bold tabular-nums pr-5 rounded-lg bg-muted/10 border-primary/40 text-xs" />
                                  <span className="absolute right-1.5 top-1/2 -translate-y-1/2 text-[9px] font-bold text-muted-foreground">€</span>
                                </div>
                              ) : (
                                <span onClick={() => startInlinePriceEdit(dish)} className="text-sm font-bold tabular-nums cursor-text py-1 px-2 rounded-lg hover:bg-muted transition-colors border border-transparent hover:border-muted-foreground/10">{formatPrice(dish.price || 0)}</span>
                              )}
                            </div>
                            <Button variant="ghost" size="icon" className={cn("h-8 w-8 rounded-lg", dish.soldOut ? "text-success" : "text-destructive")} onClick={() => toggleSoldOut(dish)}>
                              {dish.soldOut ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}
                      </Button>
                  </div>
                </div>
              ))}
                      <button onClick={() => { setEditingDishId(null); setIsDishEditorOpen(true); }} className="w-full h-10 flex items-center gap-2 px-4 text-muted-foreground hover:bg-muted/10 hover:text-primary transition-all group border-t border-muted-foreground/5 bg-muted/5 font-bold uppercase tracking-widest text-[9px]">
                        <Plus className="h-3.5 w-3.5" /> Add item to {cat?.name || "category"}
                      </button>
            </div>
                  )}
              </div>
              );
            })}
            </div>
        </TabsContent>

        <TabsContent value="categories" className="mt-0 space-y-6">
          <div className="flex justify-end"><Button onClick={() => { setEditingCategory(null); setIsCategoryEditorOpen(true); }} size="sm" className="h-9 px-4 font-bold rounded-lg"><Plus className="w-4 h-4 mr-2" /> New Category</Button></div>
          <div className="grid grid-cols-1 gap-2 border rounded-2xl overflow-hidden bg-muted-foreground/10 border-muted-foreground/10">
            {categories.map(cat => (
              <div key={cat.id} className="flex items-center justify-between p-4 bg-background hover:bg-muted/5 transition-all cursor-pointer border-b last:border-b-0 border-muted-foreground/5" onClick={() => { setEditingCategory(cat); setIsCategoryEditorOpen(true); }}>
                <div className="flex items-center gap-4">
                  <div className="h-9 w-9 rounded-lg bg-muted/20 flex items-center justify-center text-muted-foreground/40"><Layers className="w-5 h-5" /></div>
                  <span className="font-bold text-sm">{cat.name}</span>
                </div>
                <ChevronRight className="w-4 h-4 text-muted-foreground" />
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="customizations" className="mt-0 space-y-6">
          <div className="flex justify-end"><Button onClick={() => { setEditingOption(null); setIsOptionEditorOpen(true); }} size="sm" className="h-9 px-4 font-bold rounded-lg"><Plus className="w-4 h-4 mr-2" /> New Modifier Group</Button></div>
          <div className="grid grid-cols-1 gap-2 border rounded-2xl overflow-hidden bg-muted-foreground/10 border-muted-foreground/10">
            {options.map(opt => (
              <div key={opt.id} className="flex items-center justify-between p-4 bg-background hover:bg-muted/5 transition-all cursor-pointer border-b last:border-b-0 border-muted-foreground/5" onClick={() => { setEditingOption(opt); setIsOptionEditorOpen(true); }}>
                <div className="flex items-center gap-4">
                  <div className="h-9 w-9 rounded-lg bg-muted/20 flex items-center justify-center text-muted-foreground/40"><List className="w-5 h-5" /></div>
          <div>
                    <span className="font-bold text-sm block">{opt.title}</span>
                    <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest">{opt.dishIDs?.length || 0} Products</span>
                  </div>
                </div>
                <ChevronRight className="w-4 h-4 text-muted-foreground" />
          </div>
            ))}
          </div>
        </TabsContent>
      </Tabs>

      {/* Drawers */}
      <Sheet open={isDishEditorOpen} onOpenChange={setIsDishEditorOpen}>
        {restaurant?.id && <DishEditorDrawer dishId={editingDishId} restaurantId={restaurant.id} categories={categories} onClose={() => setIsDishEditorOpen(false)} onSaved={fetchData} onDeleted={() => { setIsDishEditorOpen(false); fetchData(); }} />}
      </Sheet>

      <Sheet open={isCategoryEditorOpen} onOpenChange={setIsCategoryEditorOpen}>
        {restaurant?.id && <CategoryEditorDrawer category={editingCategory} restaurantId={restaurant.id} onClose={() => setIsCategoryEditorOpen(false)} onSaved={fetchData} onDeleted={() => { setIsCategoryEditorOpen(false); fetchData(); }} />}
      </Sheet>

      <Sheet open={isOptionEditorOpen} onOpenChange={setIsOptionEditorOpen}>
        {restaurant?.id && <OptionEditorDrawer option={editingOption} restaurantId={restaurant.id} onClose={() => setIsOptionEditorOpen(false)} onSaved={fetchData} onDeleted={() => { setIsOptionEditorOpen(false); fetchData(); }} />}
      </Sheet>
    </div>
  );
}
